<template>
  <!-- eslint-disable max-len -->
  <BaseCard
    :id="`dataset-${dataset.name}`"
    ref="card"
    :title="cardTitle"
    class="dataset-card"
  >
    <template #body>
      <BaseTextExpander v-if="dataset.description" :text="dataset.description" class="mt-2" />
      <div class="card-detail-box-row mt-2">
        <div class="card-detail-box">
          <div class="card-detail-box__badge">
            <div class="card-detail-box__icon">
              <SVGIcon
                :iconName="'image'"
                :width="'24px'"
                :height="'24px'"
              />
            </div>
            <div class="card-detail-box__details">
              <h3 class="card-detail-box__details-title">Images</h3>
              <span class="card-detail-box__details-value">{{ dataset.numImages ? dataset.numImages : '--' }}</span>
            </div>
          </div>
        </div>
        <div class="card-detail-box">
          <div class="card-detail-box__badge">
            <div class="card-detail-box__icon">
              <SVGIcon
                :iconName="'tag'"
                :width="'24px'"
                :height="'24px'"
              />
            </div>
            <div class="card-detail-box__details">
              <h3 class="card-detail-box__details-title">Labels</h3>
              <span class="card-detail-box__details-value">{{ dataset.numLabels ? dataset.numLabels : '--' }}</span>
              <div class="card-detail-box__icon-container">
                <DatasetCardLabelsPopper
                  v-if="!isParked"
                  :dataset="dataset"
                  :labels="labels"
                  :disableDelete="!canWrite"
                  :canWrite="canWrite"
                  :permissions="permissions"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-detail-box _groups mt-2">
        <div class="card-detail-box__badge _bottom-border">
          <div v-tooltip="`Groups are useful to separate images into 'train' and 'validate' groups for workflow.`" class="card-detail-box__icon">
            <SVGIcon
              :iconName="'split'"
              :width="'24px'"
              :height="'24px'"
            />
          </div>
          <div class="card-detail-box__details">
            <h3 class="card-detail-box__details-title">Groups</h3>
            <span class="card-detail-box__details-value">{{ dataset.groups ? dataset.groups.length : '--' }}</span>
          </div>
        </div>
        <div v-if="dataset.groups && dataset.groups.length > 0" class="group-li-container scrollbar">
          <GroupListItem
            v-for="(g,i) in dataset.groups"
            :key="i"
            :name="g.name"
            :count="g.count"
            :canWrite="canWrite"
          />
        </div>
      </div>

      <div class="card-detail-box mt-2">
        <div class="card-detail-box__badge _bottom-border">
          <div
            v-tooltip="`Annotation sets are collections of annotation and sensor data for images in the dataset.
              One image can have annotations and sensro data that belong to different annotation sets.
              This is usefull for comparing outputs from different sources.`"
            class="card-detail-box__icon"
          >
            <SVGIcon
              :iconName="'set'"
              :width="'24px'"
              :height="'24px'"
            />
          </div>
          <div class="card-detail-box__details">
            <h3 class="card-detail-box__details-title">Annotation Sets</h3>
            <span class="card-detail-box__details-value">{{ numAnnotationSets }}</span>
          </div>
        </div>
        <div class="annset-li-container scrollbar">
          <AnnotationSetListItem
            v-for="(s,i) in internalAnnotationSets"
            :key="i"
            v-model:numImg="s.num_images"
            v-model:numAnnotations="s.num_annotations"
            v-model:hashId="s.hashed_id"
            :annotationSet="s"
            :isFrozen="isFrozen"
            :canWrite="canWrite"
            @generate-hash="$emit('generate-hash', s)"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="footer">
        <EncodedIdField
          :id="dataset.id"
          :type="'dataset'"
        />
        <div>
          <div class="footer-icons">
            <SVGIcon
              v-if="!canWrite"
              v-tooltip="'Read Only Dataset - Copy to another dataset to make changes or add trainers'"
              :iconName="'pencil_off'"
              :width="'24px'"
              :height="'24px'"
              class="footer-icon ms-2"
            />
          </div>
          <div v-if="dataset.size > 0" class="dataset-size">
            Size: {{ formatBytes(dataset.size) }}
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>
<script>
import BaseCard from '@/components/Cards/BaseCard.vue';
import BaseTextExpander from '@/components/BaseTextExpander.vue';
import AnnotationSetListItem from '@/components/DatasetComponent/DatasetManagement/AnnotationSetListItem.vue';
import GroupListItem from '@/components/DatasetComponent/DatasetManagement/GroupListItem.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import DatasetCardLabelsPopper from '@/components/DatasetComponent/DatasetCardLabelsPopper.vue';
import SVGIcon from '@/components/SVGIcon.vue';

export default {
  name: 'PublicDatasetCard',
  components: {
    DatasetCardLabelsPopper,
    SVGIcon,
    BaseCard,
    BaseTextExpander,
    AnnotationSetListItem,
    GroupListItem,
    EncodedIdField,
  },
  props: {
    dataset: {
      type: Object,
      default: () => {},
    },
    annotationSets: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    permissions: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'delete-dataset',
    'update:annotation-sets',
    'update:labels',
    'dismiss-error',
    'generate-hash',
  ],
  computed: {
    canWrite() {
      return false;
    },
    canRead() {
      return true;
    },
    isFrozen() {
      return this.dataset.is_frozen;
    },
    isParked() {
      return this.dataset && this.dataset['parked_at'] !== "0001-01-01T00:00:00Z";
    },
    internalAnnotationSets: {
      get() { return this.annotationSets; },
      set(v) { this.$emit('update:annotation-sets', v); },
    },
    cardTitle() {
      if (this.dataset?.name) {
        return this.dataset.name;
      }
      return '';
    },
    numAnnotationSets() {
      if (this.internalAnnotationSets && this.internalAnnotationSets.length) {
        return this.internalAnnotationSets.length;
      }
      return 0;
    },
  },
  watch: {
    dataset: {
      deep: true,
      handler(d) {
        if (d && d.annotation_sets) {
          this.internalAnnotationSets = d.annotation_sets;
        }
      },
    },
  },
  methods: {
    formatBytes(bytes) {
      const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      if (bytes === 0) return '0 Bytes';

      const i = Math.floor(Math.log(bytes) / Math.log(1000));
      const size = bytes / 1000 ** i;

      return `${size.toFixed(2)} ${units[i]}`;
    },
  },
};
</script>

<style lang="scss" scoped>

.dataset-card {
  margin: 2px;
}

.card:not([disabled="true"]):hover {
  @include themify() {
    outline: 2px solid themed('card-border-color-hover');
  }
}
.card:not([disabled="true"]).dataset-selected {
  // margin: 3px;
  @include themify() {
    outline: 3px solid themed('card-list-color-selected-border')
  }
}

.card .create-annset-icon {
  margin-left: 4px;
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.card .create-label-icon {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.card:hover .copy-icon:hover,
.card:hover .create-annset-icon:hover,
.card:hover .refresh-icon:hover {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.annset-li-container {
  overflow-y: auto;
  font-size: 0.875rem;
  flex: 1 1;
}

.group-li-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, 140px);
  grid-auto-rows: 65px;
  width: 100%;
  gap: 5px;
  padding: 5px;
  overflow-y: auto;
  flex: 1 1 auto;
}

$badge-height: 50px;
$badge-padding: 8px;
.card-detail-box-row {
  display: flex;
  flex-direction: row;
  min-height: $badge-height;
  max-height: $badge-height;
  width: 100%;
  gap: 0.5rem;
}
.card-detail-box {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 1 100%;
  border: 1px solid rgba(0,0,0,0.075);
  border-radius: 4px;
  overflow: hidden;
  @include themify() {
    background: themed('color-white-600');
  }

  &__badge {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    height: $badge-height - 2px;
    width: 100%;
    padding: $badge-padding;

    &._bottom-border {
      border-bottom: 1px solid rgba(0, 0, 0, 0.075);
    }
  }

  &__icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    min-width: $badge-height - ($badge-padding * 2) - 2px;
    height: 100%;
    @include themify() {
      background: themed('color-primary');
    }

    svg {
      color: white;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 100%;
    border-radius: 4px;
    margin-left: 12px;
    overflow: hidden;
  }

  &__details-title {
    line-height: 1;
    font-weight: 500;
    @include themify() {
      font-size: 0.75rem;
      color: themed('body-text-color-secondary');
    }
  }

  &__details-value {
    margin-top: auto;
    line-height: 1;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @include themify() {
      font-size: 1.125rem;
      color: themed('body-text-color');
    }
  }

  &__icon-container {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &._groups {
    max-height: 130px;
    flex: 1 0 min-content;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  align-items: center;
  width: 100%;
}

.footer-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: fit-content;
  justify-content: flex-end;
  margin-top: 5px;
}

.footer-icon {
  color: var(--color-primary);
}

.dataset-size {
  font-size: 0.8rem;
  color: var(--body-text-color-secondary);
}

</style>

